import { useCallback, useMemo } from 'react';
import { api, componentApi } from '@ardoq/api';
import { getArdoqErrorMessage, isArdoqError } from '@ardoq/common-helpers';
import { type I18nLocale } from '@ardoq/i18n';
import {
  buildGenerateDescriptionLabels,
  useGenerateDescriptionState,
  type GenerateDescriptionLabels,
} from '@ardoq/rich-text-editor';
import type {
  ComponentDescriptionTracking,
  ComponentToDescribe,
} from './types';

type UseGenerateComponentDescriptionArgs = {
  component: ComponentToDescribe;
  currentValue: string;
  i18nLocale: I18nLocale;
  tracking: ComponentDescriptionTracking;
  onGenerated: (text: string) => void;
};

type UseGenerateComponentDescriptionResult = {
  generateDescription: () => Promise<void>;
  isLoading: boolean;
  hasError: boolean;
  errorMessage?: string;
  warningMessage?: string;
  isMissingItemToDescribe: boolean;
  labels: GenerateDescriptionLabels;
};

/**
 * Owns the "generate a component description with AI" side effect: the API call,
 * loading/error/warning state, and analytics.
 */
export const useGenerateComponentDescription = ({
  component,
  currentValue,
  i18nLocale,
  tracking,
  onGenerated,
}: UseGenerateComponentDescriptionArgs): UseGenerateComponentDescriptionResult => {
  const labels = useMemo(
    () => buildGenerateDescriptionLabels(i18nLocale),
    [i18nLocale]
  );
  const { state, commands } = useGenerateDescriptionState(
    labels.defaultErrorMessage
  );
  const { name, typeName, workspaceName } = component;

  // Wrapped in useCallback so the identity only changes when the inputs it
  // closes over do, which is what lets a caller capture it in a mount-only
  // effect. That holds only for callers that pass a stable `tracking` and
  // `onGenerated`; `commands` is memoised by useGenerateDescriptionState.
  const generateDescription = useCallback(async (): Promise<void> => {
    if (!name || !typeName) {
      return;
    }
    commands.onLoading();
    tracking.onGenerationAttempted();
    const response = await componentApi.generateDescription({
      name,
      type: typeName,
      description: currentValue,
      workspaceName,
    });
    commands.onDescriptionReceived();
    if (isArdoqError(response)) {
      commands.onError(getArdoqErrorMessage(response));
      api.logErrorIfNeeded(response);
    } else if (response.type === 'description') {
      tracking.onGenerationSucceeded();
      onGenerated(response.text);
    } else {
      commands.onLLMCouldNotGenerateDescription(response.text);
      tracking.onGenerationDeclinedByLlm(response.text);
    }
  }, [
    name,
    typeName,
    workspaceName,
    currentValue,
    commands,
    onGenerated,
    tracking,
  ]);

  return {
    generateDescription,
    isLoading: state.isLoading,
    hasError: state.hasError,
    errorMessage: state.errorMessage,
    warningMessage: state.reasonForNotBeingAbleToGenerateDescription,
    isMissingItemToDescribe: !name || !typeName,
    labels,
  };
};
